import React from 'react';
import { Alert, Button, List, Modal, Popconfirm, Space, Tag, Typography } from 'antd';
import { ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import type {
  GenerateAndScheduleResponse,
} from '@/services/blogCreationService';
import { duplicateSkips } from './batchScheduleDuplicateUtils';
import { useResizableAntdModal } from '@/hooks/useResizableAntdModal';

const { Text } = Typography;

function formatStatus(status?: string | null): string {
  if (!status) return 'Active';
  return status === 'in_progress' ? 'Generating' : status.replace(/_/g, ' ');
}

function formatCreatedAt(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface BatchScheduleDuplicateDialogProps {
  open: boolean;
  result: GenerateAndScheduleResponse | null;
  loading?: boolean;
  onClose: () => void;
  onViewExisting: (contentId: string) => void;
  onDeleteAndRetry: () => void;
}

export function BatchScheduleDuplicateDialog({
  open,
  result,
  loading = false,
  onClose,
  onViewExisting,
  onDeleteAndRetry,
}: BatchScheduleDuplicateDialogProps) {
  const duplicates = duplicateSkips(result);
  const scheduledCount = result?.scheduled_posts?.length ?? 0;
  const duplicateIds = duplicates
    .map(item => item.content_id)
    .filter((id): id is string => Boolean(id));
  const firstDuplicateId = duplicateIds[0];
  const allSkipped = scheduledCount === 0 && duplicates.length > 0;

  const resizable = useResizableAntdModal({
    storageKey: 'batch-schedule-duplicate-modal-size-v1',
    defaultWidth: 640,
    defaultHeight: 400,
  });

  return (
    <Modal
      title={allSkipped ? 'These posts already exist' : 'Some posts already exist'}
      open={open}
      onCancel={onClose}
      {...resizable}
      footer={
        <Space wrap>
          <Button onClick={onClose}>
            {allSkipped ? 'Choose Different Keywords' : 'Close'}
          </Button>
          <Button
            icon={<ExternalLink className="h-4 w-4" />}
            disabled={!firstDuplicateId}
            onClick={() => firstDuplicateId && onViewExisting(firstDuplicateId)}
          >
            View Existing
          </Button>
          <Popconfirm
            title="Delete existing drafts and generate again?"
            description="This removes the active duplicate records first, then reruns this same batch."
            okText="Delete and Generate"
            okButtonProps={{ danger: true, loading }}
            onConfirm={onDeleteAndRetry}
          >
            <Button
              danger
              icon={loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              disabled={duplicateIds.length === 0}
              loading={loading}
            >
              Delete and Generate Again
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      <div className="space-y-4">
        <Alert
          type={allSkipped ? 'warning' : 'info'}
          showIcon
          message={
            allSkipped
              ? `No new posts were started because all ${duplicates.length} selected keyword${duplicates.length === 1 ? '' : 's'} already have active drafts.`
              : `${scheduledCount} post${scheduledCount === 1 ? '' : 's'} started. ${duplicates.length} keyword${duplicates.length === 1 ? '' : 's'} already had active drafts.`
          }
          description="The batch was blocked for these keywords to avoid creating duplicate pending or generating content. You can open what already exists, delete those active drafts and rerun the same batch, or choose different keywords."
        />

        <List
          size="small"
          bordered
          dataSource={duplicates}
          renderItem={(item) => {
            const createdAt = formatCreatedAt(item.created_at);
            return (
              <List.Item
                actions={
                  item.content_id ? [(
                    <Button
                      key="open"
                      size="small"
                      type="link"
                      onClick={() => onViewExisting(item.content_id as string)}
                    >
                      Open
                    </Button>
                  )] : []
                }
              >
                <List.Item.Meta
                  title={
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{item.keyword}</span>
                      <Tag color={item.status === 'in_progress' ? 'processing' : 'default'}>
                        {formatStatus(item.status)}
                      </Tag>
                    </div>
                  }
                  description={
                    <Space size={8} wrap>
                      <Text type="secondary">Duplicate active draft</Text>
                      {createdAt && <Text type="secondary">Created {createdAt}</Text>}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </div>
    </Modal>
  );
}
